import { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import type { AdminMasterEntry, MastersGateway, SaveMasterEntryInput } from '@forq/masters-data';

interface DraftState {
  readonly id: string | null;
  readonly code: string;
  readonly name: string;
  readonly active: boolean;
  readonly sortOrder: number;
}
const emptyDraft: DraftState = { id: null, code: '', name: '', active: true, sortOrder: 0 };

function messageOf(error: unknown): string {
  return error instanceof Error && error.message ? error.message : 'Der Vorgang ist fehlgeschlagen.';
}

function MasterList({ title, description, load, save, remove, canManage }: {
  readonly title: string;
  readonly description: string;
  readonly load: () => Promise<readonly AdminMasterEntry[]>;
  readonly save: (input: Omit<SaveMasterEntryInput, 'tenantId'>) => Promise<void>;
  readonly remove: (id: string) => Promise<void>;
  readonly canManage: boolean;
}) {
  const [entries, setEntries] = useState<readonly AdminMasterEntry[]>([]);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    load().then(setEntries).catch((cause) => setError(messageOf(cause)));
  }, [load]);
  useEffect(() => { reload(); }, [reload]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await save({ id: draft.id, code: draft.code, name: draft.name, active: draft.active, sortOrder: draft.sortOrder });
      setDraft(emptyDraft);
      reload();
    } catch (cause) { setError(messageOf(cause)); } finally { setBusy(false); }
  };
  const del = async (id: string) => {
    setBusy(true); setError(null);
    try { await remove(id); reload(); } catch (cause) { setError(messageOf(cause)); } finally { setBusy(false); }
  };

  return (
    <section className="admin-master">
      <header className="admin-master__head"><h3>{title}</h3><p>{description}</p></header>
      {error ? <p className="admin-master__error" role="alert">{error}</p> : null}
      {canManage ? (
        <form className="admin-master__form" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          <label>Code<input value={draft.code} maxLength={20} required onChange={(event) => setDraft((current) => ({ ...current, code: event.target.value }))} /></label>
          <label>Name<input value={draft.name} maxLength={80} required onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} /></label>
          <label>Reihenfolge<input type="number" value={draft.sortOrder} onChange={(event) => setDraft((current) => ({ ...current, sortOrder: Number(event.target.value) }))} /></label>
          <label className="admin-master__check"><input type="checkbox" checked={draft.active} onChange={(event) => setDraft((current) => ({ ...current, active: event.target.checked }))} /> Aktiv</label>
          <div className="admin-master__actions">
            <button type="submit" className="admin-master__save" disabled={busy}><Save size={13} /> {draft.id ? 'Änderung speichern' : 'Anlegen'}</button>
            {draft.id ? <button type="button" className="admin-master__ghost" onClick={() => setDraft(emptyDraft)}><Plus size={13} /> Neu stattdessen</button> : null}
          </div>
        </form>
      ) : null}
      <ul className="admin-master__list">
        {entries.length === 0 ? <li className="admin-empty">Noch keine Einträge.</li> : entries.map((entry) => (
          <li key={entry.id} className={entry.active ? '' : 'is-inactive'}>
            <div className="admin-master__item-main">
              <strong>{entry.name}</strong>
              <small>{entry.code}{entry.active ? '' : ' · inaktiv'}</small>
            </div>
            {canManage ? (
              <div className="admin-master__item-actions">
                <button type="button" onClick={() => setDraft({ id: entry.id, code: entry.code, name: entry.name, active: entry.active, sortOrder: entry.sortOrder })}>Bearbeiten</button>
                <button type="button" className="admin-master__delete" disabled={busy} onClick={() => void del(entry.id)}><Trash2 size={12} /> Löschen</button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Pflege der beiden Stämme Kostenstellen + Warengruppen. Kostenstelle wird je Beleg gewählt,
 * Warengruppe je Ware/Leistung zugeordnet -- die Auswertung teilt Umsatz/Kosten darauf auf.
 */
export function MastersFeature({ tenantId, gateway, canManage }: {
  readonly tenantId: string;
  readonly gateway: MastersGateway;
  readonly canManage: boolean;
}) {
  return (
    <div className="admin-masters">
      <MasterList
        title="Kostenstellen"
        description="Je Beleg wählbar. Umsätze/Kosten werden im Report darauf aufgeteilt; im DATEV-Export als KOST1."
        load={() => gateway.adminListCostCenters(tenantId)}
        save={(input) => gateway.saveCostCenter({ ...input, tenantId })}
        remove={(id) => gateway.deleteCostCenter(tenantId, id)}
        canManage={canManage}
      />
      <MasterList
        title="Warengruppen"
        description="Je Ware/Leistung (Artikel, Maschine, Dienstleistung) zuordenbar. Der Report zeigt Umsatz je Warengruppe."
        load={() => gateway.adminListWarengruppen(tenantId)}
        save={(input) => gateway.saveWarengruppe({ ...input, tenantId })}
        remove={(id) => gateway.deleteWarengruppe(tenantId, id)}
        canManage={canManage}
      />
    </div>
  );
}
